'use client';

import './nav-card.scss';

import { useTranslation } from 'react-i18next';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.js';
import {
  FlexibleContentProps,
  SacFilterProps,
} from '@sola/schema/lib/schema.js';
import { useRandomChild } from '@sola/skippy-react/lib/random-child/context.js';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';

import { Image } from '../../atoms/image/image';
import { ChildBioSummary } from '../child-bio-details/child-bio-summary.js';
import { FeatureBase } from '../feature-base/feature-base';
import { NavCardSkeleton } from './nav-card-skeleton';

type NavCardProps = FlexibleContentProps & { className?: string };
type NavCardSacProps = SacFilterProps & { className?: string };

const NavCardContent: React.FC<NavCardProps> = ({
  media,
  subtitle: _subtitle,
  icon: _icon,
  quotation: _quotation,
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        'NavCard w-full max-w-sm mx-auto flex flex-cols items-center',
        className,
      )}
    >
      {media?.image && (
        <div className="NavCard__media">
          <Image
            image={media?.image}
            hasPlaceholder
            aspectRatio="square"
            objectPosition="top"
            rounded="md"
          />
        </div>
      )}

      <div className="NavCard__content">
        <FeatureBase
          {...props}
          titleSize="lg"
          titleVariant="label"
          bodySize="md"
          bodyVariant="label"
          buttonSize="md"
          primaryCTAVariant="tertiary"
          primaryCTAIconName="arrow-right"
          primaryCTAIconPlacement="after"
          className={cn('NavCard__body')}
          variantSpacing="card"
          alignment="left"
          inline
          buttonsAsLinks
        />
      </div>
    </div>
  );
};

const NavCardSac = (props: NavCardSacProps) => {
  const { className } = props;
  const { t } = useTranslation(['bio', 'geo']);
  const { data: childData, isLoading } = useRandomChild();

  const countryIso = countryToIso(childData?.country, childData?.localId);
  const countryKey = `geo:countries.${countryIso}.nameS`;
  const translatedCountry = t(countryKey);

  const cardProps: FlexibleContentProps = childData
    ? {
        title: childData.name.preferred,
        body: (
          <ChildBioSummary
            className="NavCard__body"
            short
            details={childData}
            translatedCountry={translatedCountry}
          />
        ),
        media: {
          image: {
            url: childData.imageUrl,
            height: 124,
            width: 124,
            ratio: 'square',
            alt: t('bio:picAlt', {
              childName: childData.name.preferred,
            }),
          },
        },
        primaryCTA: {
          title: t('bio:title', {
            childName: childData.name.preferred,
          }),
          link: `${knownSkippyUrls.childBio}?gid=${childData.globalId}`,
        },
      }
    : props;

  return isLoading ? (
    <NavCardSkeleton className={className} />
  ) : (
    <NavCardContent {...cardProps} className={className} />
  );
};

export const NavCard = ({ ...props }: NavCardProps | NavCardSacProps) => {
  if ('limit' in props) {
    return <NavCardSac {...props} />;
  }

  return <NavCardContent {...props} />;
};
